import { Fragment, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { COLORS } from '../../constants/theme';
import { formatNumber, pct } from '../../utils/format';
import { StatCard } from '../ui/StatCard';
import { ratioWidthPercent } from '../shell/HubCard';
import { FlowSection, ModCell } from './FlowSection';
import { GroupCard, DataRow, T2Row, CardSeparator, TableSeparator, DataTriple, NetRow } from './EconomyCards';
import {
  computeArmy,
  computeUnfilledJobs,
  getEmploymentStatus,
  getCapacityStatus,
  NET_CHANGE_GROUPS,
} from '../../utils/economyStats';
import { buildGoldRows, buildFoodRows, buildRunesRows, buildModifiers, signed, SOURCE_COLORS, ROW_HOVER_STYLE } from '../../utils/economyFlow';
import type { EconomyShellProps } from '../../pages/Economy';

/**
 * SF-164: desktop content for the merged Economy page — largely `EconomyTab`/`ResourceFlowsTab`'s
 * existing desktop markup relocated, plus the new Max Population/Capacity row (both cards).
 *
 * `Province.economy.taxRate` is points-scale (e.g. `10.0` means "10%"), not a 0.0–1.0 fraction —
 * confirmed against `EconomySystem.java`'s `taxMultiplier = 1.0 + taxRate/100.0` and the
 * pre-existing `councilCalculations.test.ts` fixtures (`taxRate: 10.0 // 10% tax bonus`). The
 * spec's Phase 3 "Tax Rate double-vs-percent bug" decision was itself wrong (a ×100 "fix" was
 * shipped and caught in Phase 4 rule-compliance review) — render the raw points value directly,
 * matching `councilCalculations.ts`'s own scale assumption and the original (never-buggy) markup.
 */
export function DesktopEconomyShell({ province: p }: EconomyShellProps) {
  const location = useLocation();
  const h1Ref = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    h1Ref.current?.focus();
  }, [location.pathname]);

  const gold = useMemo(() => buildGoldRows(p), [p]);
  const food = useMemo(() => buildFoodRows(p), [p]);
  const runes = useMemo(() => buildRunesRows(p), [p]);
  const modifiers = useMemo(() => buildModifiers(p), [p]);

  const army = computeArmy(p.population);
  const emp = p.economy.employment;
  const unfilledJobs = computeUnfilledJobs(emp);
  const empStatus = getEmploymentStatus(emp.employmentRate);
  const capacityStatus = getCapacityStatus(p.population.capacityUnitCount ?? NaN, p.population.maxPopulation);
  const capacityBarWidth = capacityStatus
    ? ratioWidthPercent(p.population.capacityUnitCount as number, p.population.maxPopulation as number)
    : 0;
  const capacityPercent = capacityStatus
    ? Math.round(((p.population.capacityUnitCount as number) / (p.population.maxPopulation as number)) * 100)
    : null;

  const tc = p.economy.lastTickChanges;
  const curr = p.economy.currentMonth;
  const prev = p.economy.previousMonth;

  const salutation = p.metadata.ruler.gender === 'FEMALE' ? 'Lady' : 'Sir';
  const rulerName = p.metadata.ruler.name;

  return (
    <div className="max-w-[1040px] mx-auto space-y-4">
      <style>{ROW_HOVER_STYLE}</style>

      <h1 ref={h1Ref} tabIndex={-1} className="text-xl font-bold text-game-50 outline-none">
        Economy
      </h1>

      <p className="text-sm" style={{ color: COLORS.text.tertiary }}>
        {salutation} {rulerName}, here's how {p.metadata.name}'s economy is doing today.
      </p>

      {/* ── Population / Employment ──────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <GroupCard title="Our Population">
          <DataRow label="Peasants" value={formatNumber(p.population.peasants)} />
          <DataRow label="Army" value={formatNumber(army)} />
          <DataRow label="Thieves" value={formatNumber(p.population.thieves)} />
          <DataRow label="Wizards" value={formatNumber(p.population.wizards)} />
          <CardSeparator />
          <T2Row label="Total" value={formatNumber(p.population.total)} />
          <DataRow
            label="Max Population"
            value={p.population.maxPopulation != null ? formatNumber(p.population.maxPopulation) : '—'}
          />
          {capacityStatus && (
            <div
              aria-hidden="true"
              style={{ height: 4, borderRadius: 2, overflow: 'hidden', background: COLORS.cardAlt, margin: '4px 0' }}
            >
              <div style={{ height: '100%', borderRadius: 2, width: `${capacityBarWidth}%`, backgroundColor: capacityStatus.color }} />
            </div>
          )}
          <DataRow
            label={capacityStatus ? <>Capacity (<span style={{ color: capacityStatus.color }}>{capacityStatus.label}</span>)</> : 'Capacity'}
            value={capacityPercent != null ? `${capacityPercent}%` : '—'}
            valueColor={capacityStatus?.color}
          />
        </GroupCard>

        <GroupCard title="Our Economy">
          <DataRow label="Jobs" value={formatNumber(emp.totalJobs)} />
          <DataRow label="Unemployed" value={formatNumber(emp.unemployed)} />
          <DataRow label="Unfilled Jobs" value={formatNumber(unfilledJobs)} />
          <DataRow
            label={empStatus ? <>Employment (<span style={{ color: empStatus.color }}>{empStatus.label}</span>)</> : 'Employment'}
            value={empStatus ? `${Math.round(emp.employmentRate * 100)}%` : '—'}
            valueColor={empStatus?.color}
          />
        </GroupCard>
      </div>

      {/* ── Stat Cards ────────────────────────────────────────── */}
      <div className="grid grid-cols-5 gap-3">
        <StatCard
          label="Net Gold"
          value={signed(gold.net, 'gc')}
          sub="per day"
          color={gold.net >= 0 ? COLORS.positive : COLORS.negative}
        />
        <StatCard
          label="Net Food"
          value={signed(food.net, '')}
          sub="bushels per day"
          color={food.net >= 0 ? COLORS.positive : COLORS.negative}
        />
        <StatCard
          label="Net Runes"
          value={signed(runes.net, '')}
          sub="runes per day"
          color={runes.net >= 0 ? COLORS.positive : COLORS.negative}
        />
        <StatCard
          label="Tax Rate"
          value={`${Math.round(p.economy.taxRate)}%`}
          sub="income bonus"
        />
        <StatCard
          label="Building Efficiency"
          value={pct(p.economy.buildingEfficiency)}
          sub="affects production"
        />
      </div>

      {/* ── Net Changes ───────────────────────────────────────── */}
      <div
        className="rounded-lg overflow-hidden"
        style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
      >
        <div
          className="text-center text-[11px] font-bold uppercase tracking-widest px-3.5 py-2"
          style={{ backgroundColor: COLORS.sectionHeader, color: COLORS.text.primary, borderBottom: `1px solid ${COLORS.border}` }}
        >
          Net Changes
        </div>
        <div className="px-3.5 py-2.5">
          <table className="w-full table-fixed" style={{ borderCollapse: 'collapse' }}>
            <colgroup>
              <col style={{ width: '33%' }} />
              <col style={{ width: '22.33%' }} />
              <col style={{ width: '22.33%' }} />
              <col style={{ width: '22.33%' }} />
            </colgroup>
            <thead>
              <tr>
                <th scope="col" className="text-left pb-2 text-[10px] font-normal uppercase tracking-wide" style={{ color: COLORS.text.tertiary, borderBottom: `1px solid ${COLORS.border}` }} />
                <th scope="col" className="text-right pb-2 text-[10px] font-normal uppercase tracking-wide" style={{ color: COLORS.text.tertiary, borderBottom: `1px solid ${COLORS.border}` }}>Yesterday</th>
                <th scope="col" className="text-right pb-2 text-[10px] font-normal uppercase tracking-wide" style={{ color: COLORS.text.tertiary, borderBottom: `1px solid ${COLORS.border}` }}>This Month</th>
                <th scope="col" className="text-right pb-2 text-[10px] font-normal uppercase tracking-wide" style={{ color: COLORS.text.tertiary, borderBottom: `1px solid ${COLORS.border}` }}>Last Month</th>
              </tr>
            </thead>
            <tbody>
              {NET_CHANGE_GROUPS.map((group, groupIdx) => (
                <Fragment key={group.group}>
                  {group.rows.map((row) => (
                    <DataTriple
                      key={row.label}
                      label={row.label}
                      yesterday={row.yesterday(tc)}
                      thisMonth={curr != null ? row.thisMonth(curr) : null}
                      lastMonth={prev != null ? row.lastMonth(prev) : null}
                    />
                  ))}
                  <NetRow
                    label={group.net.label}
                    yesterday={group.net.yesterday(tc)}
                    thisMonth={curr != null ? group.net.thisMonth(curr) : null}
                    lastMonth={prev != null ? group.net.lastMonth(prev) : null}
                  />
                  {groupIdx < NET_CHANGE_GROUPS.length - 1 && <TableSeparator />}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Flow Breakdown ────────────────────────────────────── */}
      <FlowSection title="Gold Details" rows={gold.rows} color="#d4b85f" />
      <FlowSection title="Food Details" rows={food.rows} color="#7ab87a" />
      <FlowSection title="Runes Details" rows={runes.rows} color="#9a7ab8" />

      {/* ── Active Modifiers ──────────────────────────────────── */}
      <div
        className="rounded overflow-hidden"
        style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
      >
        <div
          className="grid items-center px-3 py-1.5"
          style={{ gridTemplateColumns: '160px 120px 120px 120px 1fr', backgroundColor: COLORS.sectionHeader }}
        >
          <span className="text-xs font-bold uppercase tracking-wider" style={{ color: COLORS.text.primary }}>Active Modifiers</span>
          <span className="text-center text-xs uppercase tracking-wide" style={{ color: '#d4b85f' }}>Gold</span>
          <span className="text-center text-xs uppercase tracking-wide" style={{ color: '#7ab87a' }}>Food</span>
          <span className="text-center text-xs uppercase tracking-wide" style={{ color: '#9a7ab8' }}>Runes</span>
          <span className="text-center text-xs uppercase tracking-wide" style={{ color: COLORS.text.tertiary }}>Other</span>
        </div>

        {modifiers.length === 0 ? (
          <div className="px-3 py-3 text-sm" style={{ color: COLORS.text.tertiary }}>
            No active modifiers.
          </div>
        ) : (
          modifiers.map((mod, idx) => (
            <div
              key={idx}
              className="flow-row grid items-center px-3 py-1.5"
              style={{ gridTemplateColumns: '160px 120px 120px 120px 1fr', borderBottom: `1px solid ${COLORS.border}`, transition: 'background-color 0s' }}
            >
              <span className="text-sm flex items-center gap-1.5">
                <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: SOURCE_COLORS[mod.source] }} />
                <span style={{ color: COLORS.text.primary }}>{mod.name}</span>
              </span>
              <ModCell value={mod.gold} />
              <ModCell value={mod.food} />
              <ModCell value={mod.runes} />
              <ModCell value={mod.other} />
            </div>
          ))
        )}
      </div>
    </div>
  );
}
